import type { ContentRepository } from "../../../domain/repositories/content/content-repository";
import type {
  Notice,
  Faq,
  Popup,
  Font,
  NoticeCreateRequest,
  NoticeUpdateRequest,
  FaqCreateRequest,
  FaqUpdateRequest,
  FaqOrderUpdateRequest,
  PopupReorderRequest,
  FontCreateRequest,
  FontUpdateRequest,
  Portfolio,
  PortfolioSection,
  PortfolioCreateRequest,
  PortfolioUpdateRequest,
  PortfolioReorderRequest,
  PortfolioButton,
  PortfolioLinkedOrder,
  PortfolioButtonsUpdateRequest,
  PortfolioOrderLinksUpdateRequest,
} from "../../../domain/types/content";
import { contentApiService } from "../../api/content/content-api-service";

export class ContentRepositoryImpl implements ContentRepository {
  // ========== 공지사항 ==========

  async getNotices(companyId: number): Promise<Notice[]> {
    const response = await contentApiService.getNotices(companyId);

    if (response.data) {
      return response.data;
    }

    throw new Error(response.errorMsg || "공지사항 목록 가져오기 실패.");
  }

  async getNotice(companyId: number, noticeId: number): Promise<Notice> {
    const response = await contentApiService.getNotice(companyId, noticeId);

    if (response.data) {
      return response.data;
    }

    throw new Error(response.errorMsg || "공지사항 가져오기 실패.");
  }

  async createNotice(
    companyId: number,
    request: NoticeCreateRequest
  ): Promise<Notice> {
    const response = await contentApiService.createNotice(companyId, request);

    if (response.data) {
      return response.data;
    }

    throw new Error(response.errorMsg || "공지사항 생성 실패.");
  }

  async updateNotice(
    companyId: number,
    noticeId: number,
    request: NoticeUpdateRequest
  ): Promise<Notice> {
    const response = await contentApiService.updateNotice(
      companyId,
      noticeId,
      request
    );

    if (response.data) {
      return response.data;
    }

    throw new Error(response.errorMsg || "공지사항 수정 실패.");
  }

  async deleteNotice(companyId: number, noticeId: number): Promise<void> {
    await contentApiService.deleteNotice(companyId, noticeId);
  }

  // ========== FAQ ==========

  async getFaqs(companyId: number, category?: string): Promise<Faq[]> {
    const response = await contentApiService.getFaqs(companyId, category);

    if (response.data) {
      return response.data;
    }

    throw new Error(response.errorMsg || "FAQ 목록 가져오기 실패.");
  }

  async getFaq(companyId: number, faqId: number): Promise<Faq> {
    const response = await contentApiService.getFaq(companyId, faqId);

    if (response.data) {
      return response.data;
    }

    throw new Error(response.errorMsg || "FAQ 가져오기 실패.");
  }

  async createFaq(companyId: number, request: FaqCreateRequest): Promise<Faq> {
    const response = await contentApiService.createFaq(companyId, request);

    if (response.data) {
      return response.data;
    }

    throw new Error(response.errorMsg || "FAQ 생성 실패.");
  }

  async updateFaq(
    companyId: number,
    faqId: number,
    request: FaqUpdateRequest
  ): Promise<Faq> {
    const response = await contentApiService.updateFaq(
      companyId,
      faqId,
      request
    );

    if (response.data) {
      return response.data;
    }

    throw new Error(response.errorMsg || "FAQ 수정 실패.");
  }

  async updateFaqOrder(
    companyId: number,
    faqId: number,
    request: FaqOrderUpdateRequest
  ): Promise<void> {
    await contentApiService.updateFaqOrder(companyId, faqId, request);
  }

  async deleteFaq(companyId: number, faqId: number): Promise<void> {
    await contentApiService.deleteFaq(companyId, faqId);
  }

  // ========== 팝업 ==========

  async getPopups(companyId: number): Promise<Popup[]> {
    const response = await contentApiService.getPopups(companyId);

    if (response.data) {
      return response.data;
    }

    throw new Error(response.errorMsg || "팝업 목록 가져오기 실패.");
  }

  async createPopup(
    companyId: number,
    name: string,
    isEnabled: boolean,
    popupImage?: File
  ): Promise<Popup> {
    const response = await contentApiService.createPopup(
      companyId,
      name,
      isEnabled,
      popupImage
    );

    if (response.data) {
      return response.data;
    }

    throw new Error(response.errorMsg || "팝업 생성 실패.");
  }

  async updatePopup(
    companyId: number,
    popupId: number,
    name: string,
    isEnabled: boolean,
    popupImage?: File
  ): Promise<Popup> {
    const response = await contentApiService.updatePopup(
      companyId,
      popupId,
      name,
      isEnabled,
      popupImage
    );

    if (response.data) {
      return response.data;
    }

    throw new Error(response.errorMsg || "팝업 수정 실패.");
  }

  async reorderPopups(
    companyId: number,
    request: PopupReorderRequest
  ): Promise<void> {
    await contentApiService.reorderPopups(companyId, request);
  }

  async deletePopup(companyId: number, popupId: number): Promise<void> {
    await contentApiService.deletePopup(companyId, popupId);
  }

  // ========== 폰트 ==========

  async getFonts(companyId: number): Promise<Font[]> {
    const response = await contentApiService.getFonts(companyId);

    if (response.data) {
      return response.data;
    }

    throw new Error(response.errorMsg || "폰트 목록 가져오기 실패.");
  }

  async createFont(companyId: number, request: FontCreateRequest): Promise<Font> {
    const response = await contentApiService.createFont(companyId, request);

    if (response.data) {
      return response.data;
    }

    throw new Error(response.errorMsg || "폰트 생성 실패.");
  }

  async updateFont(
    companyId: number,
    fontId: number,
    request: FontUpdateRequest
  ): Promise<Font> {
    const response = await contentApiService.updateFont(companyId, fontId, request);

    if (response.data) {
      return response.data;
    }

    throw new Error(response.errorMsg || "폰트 수정 실패.");
  }

  async deleteFont(companyId: number, fontId: number): Promise<void> {
    await contentApiService.deleteFont(companyId, fontId);
  }

  // ========== 포트폴리오 ==========

  async getPortfolios(companyId: number): Promise<Portfolio[]> {
    const response = await contentApiService.getPortfolios(companyId);

    if (response.data) {
      return response.data;
    }

    throw new Error(response.errorMsg || "포트폴리오 목록 가져오기 실패.");
  }

  async getPortfolio(companyId: number, portfolioId: number): Promise<Portfolio> {
    const response = await contentApiService.getPortfolio(companyId, portfolioId);

    if (response.data) {
      return response.data;
    }

    throw new Error(response.errorMsg || "포트폴리오 가져오기 실패.");
  }

  async createPortfolio(
    companyId: number,
    request: PortfolioCreateRequest,
    thumbnail?: File,
    images?: File[]
  ): Promise<Portfolio> {
    const response = await contentApiService.createPortfolio(
      companyId,
      request,
      thumbnail,
      images
    );

    if (response.data) {
      return response.data;
    }

    throw new Error(response.errorMsg || "포트폴리오 생성 실패.");
  }

  async updatePortfolio(
    companyId: number,
    portfolioId: number,
    request: PortfolioUpdateRequest,
    thumbnail?: File,
    images?: File[]
  ): Promise<Portfolio> {
    const response = await contentApiService.updatePortfolio(
      companyId,
      portfolioId,
      request,
      thumbnail,
      images
    );

    if (response.data) {
      return response.data;
    }

    throw new Error(response.errorMsg || "포트폴리오 수정 실패.");
  }

  async reorderPortfolios(
    companyId: number,
    request: PortfolioReorderRequest
  ): Promise<void> {
    await contentApiService.reorderPortfolios(companyId, request);
  }

  async deletePortfolio(companyId: number, portfolioId: number): Promise<void> {
    await contentApiService.deletePortfolio(companyId, portfolioId);
  }

  async getPortfolioSection(companyId: number): Promise<PortfolioSection> {
    const response = await contentApiService.getPortfolioSection(companyId);

    if (response.data) {
      return response.data;
    }

    throw new Error(response.errorMsg || "포트폴리오 섹션 설정 가져오기 실패.");
  }

  async updatePortfolioSection(
    companyId: number,
    isEnabled: boolean
  ): Promise<PortfolioSection> {
    const response = await contentApiService.updatePortfolioSection(
      companyId,
      isEnabled
    );

    if (response.data) {
      return response.data;
    }

    throw new Error(response.errorMsg || "포트폴리오 섹션 설정 변경 실패.");
  }

  async updatePortfolioButtons(
    companyId: number,
    portfolioId: number,
    request: PortfolioButtonsUpdateRequest
  ): Promise<PortfolioButton[]> {
    const response = await contentApiService.updatePortfolioButtons(
      companyId,
      portfolioId,
      request
    );

    if (response.data) {
      return response.data;
    }

    throw new Error(response.errorMsg || "포트폴리오 버튼 저장 실패.");
  }

  async updatePortfolioOrderLinks(
    companyId: number,
    portfolioId: number,
    request: PortfolioOrderLinksUpdateRequest
  ): Promise<PortfolioLinkedOrder[]> {
    const response = await contentApiService.updatePortfolioOrderLinks(
      companyId,
      portfolioId,
      request
    );

    if (response.data) {
      return response.data;
    }

    throw new Error(response.errorMsg || "포트폴리오 주문 연결 저장 실패.");
  }
}
